import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Alert,
  Select,
  Input,
  Table,
  Space,
  Row,
  Col,
  Tag,
  message,
  Tooltip
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { authAxios } from '../utils/session_utils';
import { YouTubeLiveStream } from './types';
import { formatDuration } from './utils';

const { Search } = Input;
const { Option } = Select;

interface AutoVideoSelectionComponentProps {
  stream: YouTubeLiveStream;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  distributionPlatformData?: any[];
  embedded?: boolean;
}

interface Video {
  id: string;
  ref_id: string;
  title: string;
  duration: number;
  url: string;
  file_name: string;
  language: string;
  created_at: string;
  captions?: any[];
}

const AutoVideoSelectionComponent: React.FC<AutoVideoSelectionComponentProps> = ({
  stream,
  open,
  onClose,
  onSave,
  distributionPlatformData = [],
  embedded = false
}) => {
  const [selectedDistributionIds, setSelectedDistributionIds] = useState<string[]>(
    stream.distribution_platform_ids || []
  );
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  const [durationFilter, setDurationFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Load videos when modal opens or filters change
  useEffect(() => {
    if (open && selectedDistributionIds.length > 0) {
      loadVideos();
    } else if (open && selectedDistributionIds.length === 0) {
      setVideos([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
    }
  }, [open, selectedDistributionIds, searchText, languageFilter, durationFilter, pagination.current, pagination.pageSize]);

  const loadVideos = async (
    page = pagination.current,
    pageSize = pagination.pageSize
  ) => {
    if (selectedDistributionIds.length === 0) {
      setVideos([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
      return;
    }

    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;

      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/videos/search`,
        params: {
          q: searchText || undefined,
          language: languageFilter !== 'all' ? languageFilter : undefined,
          duration: durationFilter !== 'all' ? durationFilter : undefined,
          distributionPlatformIds: selectedDistributionIds.join(','),
          order: 'random', // Random order for pool browsing
          limit: pageSize,
          offset
        }
      });

      // Keys are normalised to lowercase on the server (videos/search)
      setVideos(response.data.videos || []);
      setPagination({
        current: page,
        pageSize,
        total: response.data.total || 0
      });
    } catch (error) {
      console.error('Error loading videos:', error);
      message.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleDistributionChange = (values: string[]) => {
    setSelectedDistributionIds(values);
    // Reset to page 1 when distribution changes
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleLanguageChange = (value: string) => {
    setLanguageFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDurationChange = (value: string) => {
    setDurationFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRefreshPool = () => {
    // Reload videos with same filters to get new random order
    loadVideos(pagination.current, pagination.pageSize);
  };

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios({
        method: 'PUT',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}`,
        data: {
          video_selection_mode: 'automatic',
          distribution_platform_ids: selectedDistributionIds
        }
      });

      message.success('Distribution platforms updated successfully');
      onSave();
    } catch (error) {
      console.error('Error saving distribution platforms:', error);
      message.error('Failed to update distribution platforms');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setSelectedDistributionIds(stream.distribution_platform_ids || []);
    onClose();
  };

  const videoColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '35%',
      ellipsis: true,
      render: (text: string, record: Video) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.ref_id} • {record.file_name}
          </div>
        </div>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      width: '10%',
      render: (language: string) => (
        <Tag color="blue">{language}</Tag>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: '10%',
      render: (duration: number) => formatDuration(Number(duration) || 0),
    },
    {
      title: 'Captions',
      key: 'captions',
      width: '15%',
      render: (text: any, record: Video) => {
        if (record.captions && record.captions.length > 0) {
          return (
            <Space direction="vertical" size="small">
              {record.captions.slice(0, 2).map((caption: any) => (
                <Tag key={caption.id} color="green">
                  {caption.language} ({caption.caption_type?.toUpperCase()})
                </Tag>
              ))}
              {record.captions.length > 2 && (
                <Tag>+{record.captions.length - 2} more</Tag>
              )}
            </Space>
          );
        }
        return (
          <Tag color="orange" icon={<WarningOutlined />}>
            No Captions
          </Tag>
        );
      },
    }
  ];

  const content = (
    <>
      <Alert
        message="Automatic Video Selection"
        description="Videos are automatically selected from the pool defined by distribution platforms. Videos cannot be manually added or removed."
        type="info"
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: '16px' }}
        showIcon
      />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Distribution Platforms Selector */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            Distribution Platforms
            <Tooltip title="Select distribution platforms to define the video pool">
              <InfoCircleOutlined style={{ marginLeft: '4px', color: '#999' }} />
            </Tooltip>
          </label>
          <Select
            mode="multiple"
            placeholder="Select distribution platforms"
            value={selectedDistributionIds}
            onChange={handleDistributionChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {distributionPlatformData.map((platform: any) => (
              <Option key={platform.id} value={platform.id}>
                {platform.name}
              </Option>
            ))}
          </Select>
        </div>

        {/* Filters */}
        {selectedDistributionIds.length > 0 && (
          <>
            <Row gutter={16}>
              <Col span={8}>
                <Search
                  placeholder="Search videos..."
                  onSearch={handleSearch}
                  enterButton={<SearchOutlined />}
                  allowClear
                />
              </Col>
              <Col span={5}>
                <Select
                  placeholder="Filter by language"
                  value={languageFilter}
                  onChange={handleLanguageChange}
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Languages</Option>
                  <Option value="English">English</Option>
                  <Option value="Spanish">Spanish</Option>
                  <Option value="French">French</Option>
                </Select>
              </Col>
              <Col span={5}>
                <Select
                  placeholder="Filter by duration"
                  value={durationFilter}
                  onChange={handleDurationChange}
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Durations</Option>
                  <Option value="short">Short (&lt; 5 min)</Option>
                  <Option value="medium">Medium (5-30 min)</Option>
                  <Option value="long">Long (&gt; 30 min)</Option>
                </Select>
              </Col>
              <Col span={6}>
                <Button
                  onClick={handleRefreshPool}
                  icon={<ReloadOutlined />}
                  style={{ width: '100%' }}
                >
                  Refresh Pool
                </Button>
              </Col>
            </Row>

            {/* Video Pool Table */}
            <div>
              <div style={{ marginBottom: '8px', color: '#666' }}>
                <InfoCircleOutlined style={{ marginRight: '4px' }} />
                Videos are shown in random order. Playlist generation will also randomise videos from the pool, so that every start/stop presents different order.
              </div>
              <Table
                columns={videoColumns}
                dataSource={videos}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} videos`
                }}
                onChange={handleTableChange}
                scroll={{ y: 400 }}
              />
            </div>
          </>
        )}

        {selectedDistributionIds.length === 0 && (
          <Alert
            message="No Distribution Platforms Selected"
            description="Please select at least one distribution platform to view the video pool."
            type="warning"
            showIcon
          />
        )}
      </Space>

      {embedded && (
        <div style={{ marginTop: '16px', textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              loading={saving}
              onClick={handleSave}
            >
              Save
            </Button>
          </Space>
        </div>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Modal
      title={`Video Pool - ${stream.name}`}
      open={open}
      onCancel={handleCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSave}
        >
          Save
        </Button>
      ]}
    >
      {content}
    </Modal>
  );
};

export default AutoVideoSelectionComponent;
